import { useState } from 'react';

import { Input } from '@/lib/base/forms/input/input';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';

import type { PropertyInputProps } from './TextInput';

/**
 * Format a stored numeric value for display in the input.
 *
 * The backend round-trips Decimal-typed properties as strings with the column's
 * full decimal precision (e.g. `"30.000000"` for an integer-valued Duration
 * Minutes field, `"0.000000"` for an unset buffer). Passing those strings
 * straight to `<input type="number">` rendered the trailing zeros to the user,
 * making perfectly normal integers look like high-precision floats.
 *
 * Running the value through `Number(...)` collapses the trailing zeros while
 * preserving genuine fractional precision (`"30.500000"` → `30.5`). Empty,
 * `null`, `undefined`, and non-numeric strings fall through unchanged so the
 * input still clears, and so a malformed value from the server is at least
 * visible to the user rather than silently rewritten to `"NaN"`.
 *
 * Precision note: `Number(...)` is bounded by JS's IEEE-754 double (~15–17
 * significant digits). CRM numeric property values — Duration / Buffer /
 * Min Notice / Max Days / currency / percentage — never approach that range,
 * so this is safe here. If a future field needs full Decimal fidelity
 * (e.g. >15 significant digits, financial sub-cent precision), it should
 * render via a string-formatting helper that strips trailing zeros without
 * round-tripping through `Number`, not through this component.
 */
function formatNumberValueForInput(
  value: string | number | object | null | undefined
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return String(parsed);
    }
    return value;
  }
  // Unexpected non-primitive value (object/array). `String(value)` would
  // render `[object Object]` into a numeric input; return empty instead.
  return '';
}

export const NumberInput: React.FC<PropertyInputProps> = ({
  propertyType,
  value,
  onChange,
  disabled = false,
  inputRef,
  placeholder,
  size = 'sm',
}) => {
  const Icon = getPropertyTypeIconComponent(propertyType);
  const [inputError, setInputError] = useState<string | null>(null);

  const handleChange = (val: string) => {
    if (!val) {
      onChange(null);
      setInputError(null);
      return;
    }

    const parsed = parseFloat(val);
    // Allow partial input like "-" or "12." by not updating if it would become NaN
    // or if the string ends with a decimal point (to allow typing fractional values)
    // The browser's type="number" handles final validation
    if (isNaN(parsed) || val.endsWith('.')) {
      // Don't call onChange - let the browser maintain the input value
      if (isNaN(parsed) && val !== '-' && !val.endsWith('.')) {
        setInputError('Invalid number');
        console.warn('Invalid numeric input for property', {
          propertyType: propertyType.property_type_name,
          invalidValue: val,
        });
      } else {
        setInputError(null);
      }
      return;
    }

    // Validate and auto-normalize percentage values
    if (propertyType.field_type_name === 'percentage') {
      if (parsed < 0 || parsed > 100) {
        setInputError('Must be between 0 and 100');
        return;
      }
      // Auto-normalize: values > 1 are treated as whole percentages (e.g. 85 → 0.85)
      if (parsed > 1) {
        onChange(parsed / 100);
        setInputError(null);
        return;
      }
    }

    onChange(parsed);
    setInputError(null);
  };

  return (
    <div>
      <Input
        type='number'
        value={formatNumberValueForInput(value)}
        onChange={handleChange}
        isDisabled={disabled}
        isInvalid={inputError !== null}
        icon={Icon}
        size={size}
        aria-label={propertyType.label}
        ref={inputRef}
        placeholder={placeholder}
      />
      {inputError && (
        <div className='text-error-primary text-body-small mt-1'>
          {inputError}
        </div>
      )}
    </div>
  );
};
